// Register hierarchy panel: searchable, collapsible tree. Each item's 2D and 3D are
// INDEPENDENT - every dimension that exists (or is expected per the register) gets its
// own pill carrying both visibility (checkbox) and its own sign-off (✓ approve / ✗ flag /
// ⊘ exclude). A layer is only "done" when every expected dimension is approved.
import type { NodeAsset, RegisterNode, ReviewState } from "../api";
import { $selectedItemId, $selectedItemName } from "../store";

export type ToggleAsset = (assetId: number, visible: boolean) => Promise<void>;
export type FlyToItem = (node: RegisterNode) => void;
export type CommentItem = (node: RegisterNode) => void;

export type ReviewFilter = "all" | "flagged" | "approved" | "todo" | "excluded";
let activeFilter: ReviewFilter = "all";
let activeQuery = "";

let onToggle: ToggleAsset = async () => {};
let onFly: FlyToItem = () => {};
let onComment: CommentItem = () => {};

// assetId -> visibility checkboxes (kept in sync when a category bulk-toggles).
const chipRegistry = new Map<number, HTMLInputElement[]>();
// itemId -> redraw its read-only status dots/notes after a review change (made in the
// bottom review bar). The verdict UI lives in the bar; the tree only reflects state.
// The bar mutates the shared node.review object, so the updater just re-reads it.
const reviewUpdaters = new Map<number, () => void>();

export function refreshTreeItemReview(itemId: number): void {
  reviewUpdaters.get(itemId)?.();
}

function badgesFor(node: RegisterNode): string {
  const out: string[] = [];
  const c = node.is_conformed?.trim();
  if (c === "Unsure") {
    out.push(
      '<span class="badge warn" title="3D not yet confirmed as conforming to the actual design (register: Unsure)">design?</span>',
    );
  }
  return out.join("");
}

function descendantAssets(node: RegisterNode): NodeAsset[] {
  const out: NodeAsset[] = [...(node.assets ?? [])];
  for (const child of node.children) out.push(...descendantAssets(child));
  return out;
}

async function toggleAssets(assets: NodeAsset[], visible: boolean): Promise<void> {
  // Load in small batches (with a yield between) so showing a big group doesn't spike
  // memory/GPU all at once - that all-at-once load silently crashes the browser tab.
  for (let i = 0; i < assets.length; i += 4) {
    await Promise.all(assets.slice(i, i + 4).map((a) => onToggle(a.id, visible)));
    if (visible) await new Promise((r) => setTimeout(r, 0)); // let the frame settle
  }
  for (const a of assets) {
    for (const cb of chipRegistry.get(a.id) ?? []) cb.checked = visible;
  }
}

// Which dimensions this layer is expected to have (an asset exists OR the register
// declares it), so missing-but-expected data can still be flagged.
export function expectedDims(node: RegisterNode): ("2D" | "3D")[] {
  const out: ("2D" | "3D")[] = [];
  // Include descendant assets so a GROUP (e.g. Native Title Determination, Comms Network)
  // is reviewable/commentable as one layer, not just its individual parts.
  const all = descendantAssets(node);
  const has2d = all.some((a) => a.dimension === "2D");
  const fn2 = (node.file_name_2d ?? "").trim().toUpperCase();
  if (has2d || (fn2 && fn2 !== "N/A")) out.push("2D");
  const has3d = all.some((a) => a.dimension === "3D");
  if (has3d || ["Yes", "TBC"].includes((node.data_3d_provided ?? "").trim())) out.push("3D");
  return out;
}

export function dimState(node: RegisterNode, dim: "2D" | "3D"): ReviewState {
  const r = node.review;
  if (!r) return "not_started";
  return (dim === "2D" ? r.status_2d : r.status_3d) ?? "not_started";
}

export function dimNotes(node: RegisterNode, dim: "2D" | "3D"): string | null {
  return (dim === "2D" ? node.review?.notes_2d : node.review?.notes_3d) ?? null;
}

// Roll up the expected dimensions into one status for the filter / strikethrough.
export function overallStatus(node: RegisterNode): ReviewState {
  const dims = expectedDims(node);
  if (!dims.length) return "not_started";
  const states = dims.map((d) => dimState(node, d));
  if (states.some((s) => s === "needs_work" || s === "rejected")) return "needs_work";
  if (states.every((s) => s === "approved" || s === "excluded") && states.some((s) => s === "approved"))
    return "approved";
  if (states.every((s) => s === "excluded")) return "excluded";
  return "not_started";
}

/** One pill = one dimension: visibility checkbox (if present) + label + a READ-ONLY
 *  status dot. Returns an updater that re-reads the node's review (the verdict is set
 *  in the bottom review bar, not here). */
function buildDimPill(
  node: RegisterNode,
  dim: "2D" | "3D",
  assets: NodeAsset[],
  row: HTMLElement,
  el: HTMLElement,
  refreshOverall: () => void,
): { pill: HTMLElement; update: () => void } {
  const dimKey = dim.toLowerCase() as "2d" | "3d";
  const pill = document.createElement("span");
  pill.className = `dim-pill dim-${dimKey}`;

  if (assets.length) {
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.className = "dim-vis";
    cb.title = `Show / hide ${dim} (${assets.map((a) => a.kind).join(", ")})`;
    cb.addEventListener("click", (e) => e.stopPropagation());
    cb.addEventListener("change", async () => {
      row.classList.add("loading");
      cb.disabled = true;
      try {
        await Promise.all(assets.map((a) => onToggle(a.id, cb.checked)));
        // Reflect the toggle on every checkbox bound to these assets, so toggling a group's
        // 3D pill (e.g. Mine Pits) also ticks each child part's checkbox, and vice versa.
        for (const a of assets) {
          for (const other of chipRegistry.get(a.id) ?? []) other.checked = cb.checked;
        }
      } finally {
        cb.disabled = false;
        row.classList.remove("loading");
      }
    });
    for (const a of assets) {
      const l = chipRegistry.get(a.id) ?? [];
      l.push(cb);
      chipRegistry.set(a.id, l);
    }
    pill.append(cb);
  } else {
    pill.classList.add("ghost"); // expected but not delivered yet
  }

  const lab = document.createElement("span");
  lab.className = "dim-lab";
  lab.textContent = dim === "3D" && assets.some((a) => a.kind === "terrain") ? "⛰" : dim;
  pill.append(lab);

  const dot = document.createElement("span");
  dot.className = "review-dot";
  pill.append(dot);

  const update = () => {
    const status = dimState(node, dim);
    const notes = dimNotes(node, dim);
    dot.className = `review-dot rv-${status}`;
    pill.classList.toggle("excluded", status === "excluded");
    pill.title =
      status === "approved" ? `${dim} signed off` :
      status === "needs_work" ? `${dim} flagged: ${notes || "needs work"}` :
      status === "excluded" ? `${dim} excluded` :
      `${dim} not reviewed`;
    el.querySelector(`:scope > .review-note-display.nd-${dimKey}`)?.remove();
    if (status === "needs_work" && notes) {
      const nd = document.createElement("div");
      nd.className = `review-note-display nd-${dimKey}`;
      nd.textContent = `✗ ${dim}: ${notes}`;
      row.after(nd);
    }
    refreshOverall();
  };
  update();
  return { pill, update };
}

function renderNode(node: RegisterNode): HTMLElement {
  const el = document.createElement("div");
  el.className = `node ${node.node_type}`;
  el.dataset.label = (node.layer_name ?? "").toLowerCase();
  const hasChildren = node.children.length > 0;

  const row = document.createElement("div");
  row.className = "node-row";
  row.dataset.id = String(node.id);

  const twisty = document.createElement("span");
  twisty.className = "twisty";
  twisty.textContent = hasChildren ? "▾" : "";

  const hasAssets = node.assets && node.assets.length > 0;

  const refreshOverall = () => {
    const o = overallStatus(node);
    el.dataset.review = o;
    el.classList.toggle("excluded", o === "excluded");
  };

  // Per-dimension pills (visibility + read-only status dot) for item rows.
  let dimWrap: HTMLElement | null = null;
  if (node.node_type === "item") {
    const dims = expectedDims(node);
    if (dims.length) {
      dimWrap = document.createElement("span");
      dimWrap.className = "dim-pills";
      const updaters: (() => void)[] = [];
      for (const dim of dims) {
        // Aggregate descendant assets so a group/parent (e.g. Comms Network) counts the 3D
        // held in its child part-files - otherwise its 3D pill wrongly reads "missing (3d?)".
        const assets = descendantAssets(node).filter((a) => a.dimension === dim);
        const { pill, update } = buildDimPill(node, dim, assets, row, el, refreshOverall);
        dimWrap.append(pill);
        updaters.push(update);
      }
      reviewUpdaters.set(node.id, () => updaters.forEach((u) => u()));
    }
  }
  refreshOverall();

  // Bulk visibility toggle - ONLY for pure groups that have no per-dimension pills. When a
  // row already shows 2D/3D pills (which now toggle the whole group's 2D/3D), a separate
  // "show all" is redundant clutter, so we skip it and let the pills do the work.
  let bulkToggle: HTMLElement | null = null;
  if (node.children.length > 0 && !dimWrap) {
    const all = descendantAssets(node);
    if (all.length) {
      bulkToggle = document.createElement("button");
      bulkToggle.className = "bulk-toggle";
      bulkToggle.textContent = "show all";
      bulkToggle.title = `Show / hide all ${all.length} layers in ${node.layer_name}`;
      let on = false;
      bulkToggle.addEventListener("click", async (e) => {
        e.stopPropagation();
        const next = !on;
        // Showing very large groups (e.g. the whole site) loads hundreds of 3D layers and
        // can exhaust browser memory. Make the user opt in past a safe threshold.
        if (next && all.length > 25) {
          const ok = window.confirm(
            `Show all ${all.length} layers in "${node.layer_name}"?\n\n` +
              "Loading this many 3D layers at once is heavy and may slow or crash the tab. " +
              "It's usually better to expand the group and turn on the parts you need.",
          );
          if (!ok) return;
        }
        on = next;
        bulkToggle!.textContent = on ? "hide all" : "show all";
        row.classList.add("loading");
        try {
          await toggleAssets(all, on);
        } finally {
          row.classList.remove("loading");
        }
      });
    }
  }

  // Lyrx legend icon (real ArcGIS swatch / point glyph) with the sign-off status overlaid
  // on its corner - the dot colour follows el[data-review] (CSS), so it tracks sign-offs live.
  const iconAsset = (node.assets ?? []).find((a) => a.icon);
  let iconEl: HTMLElement | null = null;
  if (iconAsset?.icon) {
    iconEl = document.createElement("span");
    iconEl.className = "layer-icon-wrap";
    const img = document.createElement("img");
    img.className = "layer-icon";
    img.src = iconAsset.icon.web_path;
    img.alt = "";
    iconEl.append(img, Object.assign(document.createElement("span"), { className: "layer-icon-status" }));
  }

  const label = document.createElement("span");
  label.className = "node-label";
  label.textContent = node.layer_name ?? "(unnamed)";
  label.title = node.layer_name ?? ""; // full name on hover, even when truncated
  if (hasAssets) label.classList.add("has-assets");

  const badges = document.createElement("span");
  badges.className = "node-badges";
  if (node.node_type === "item") badges.innerHTML = badgesFor(node);

  const spinner = document.createElement("span");
  spinner.className = "row-spinner";

  // Comment button: fly to the layer with the pin tool armed and pre-attached.
  let commentBtn: HTMLElement | null = null;
  if (hasAssets) {
    commentBtn = document.createElement("button");
    commentBtn.className = "row-comment";
    commentBtn.textContent = "💬";
    commentBtn.title = `Comment on ${node.layer_name}: flies here with the pin tool ready`;
    commentBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      $selectedItemId.set(node.id);
      $selectedItemName.set(node.layer_name);
      document.querySelectorAll(".node-row.selected").forEach((n) => n.classList.remove("selected"));
      row.classList.add("selected");
      onComment(node);
    });
  }

  row.append(twisty);
  if (dimWrap) row.append(dimWrap);
  if (iconEl) row.append(iconEl);
  row.append(label, spinner, badges);
  if (bulkToggle) row.append(bulkToggle);
  if (commentBtn) row.append(commentBtn);
  el.append(row);
  // The dim pills' first update() ran while `row` had no parent, so its `row.after(nd)`
  // for the red issue-note no-oped. Re-run now that row is attached to el so a persisted
  // "needs_work" note renders on initial load/refresh, not just after a live edit.
  if (node.node_type === "item") reviewUpdaters.get(node.id)?.();
  const hasLocatable = hasAssets || descendantAssets(node).length > 0;
  if (hasLocatable) row.title = "Click to fly here";

  if (hasChildren) {
    const childWrap = document.createElement("div");
    childWrap.className = "children";
    for (const child of node.children) childWrap.append(renderNode(child));
    el.append(childWrap);
    twisty.addEventListener("click", (e) => {
      e.stopPropagation();
      const collapsed = el.classList.toggle("collapsed");
      twisty.textContent = collapsed ? "▸" : "▾";
    });
  }

  row.addEventListener("click", () => {
    $selectedItemId.set(node.id);
    $selectedItemName.set(node.layer_name);
    document.querySelectorAll(".node-row.selected").forEach((n) => n.classList.remove("selected"));
    row.classList.add("selected");
    if (hasLocatable) onFly(node);
  });

  return el;
}

function statusMatches(el: HTMLElement): boolean {
  if (activeFilter === "all") return true;
  const s = el.dataset.review ?? "not_started";
  if (activeFilter === "flagged") return s === "needs_work" || s === "rejected";
  if (activeFilter === "approved") return s === "approved";
  if (activeFilter === "excluded") return s === "excluded";
  return s === "not_started"; // todo
}

/** Show nodes matching the search AND review filter (ancestors of matches stay). */
function applyFilters(root: HTMLElement): void {
  const q = activeQuery.trim().toLowerCase();
  const narrowing = q !== "" || activeFilter !== "all";
  const filterEl = (el: Element): boolean => {
    if (!(el instanceof HTMLElement) || !el.classList.contains("node")) return false;
    const isItem = el.classList.contains("item");
    const textOk = !q || (el.dataset.label ?? "").includes(q);
    const selfMatch = isItem && textOk && statusMatches(el);
    let childVisible = false;
    const kids = el.querySelector(":scope > .children");
    if (kids) {
      for (const child of kids.children) {
        if (filterEl(child)) childVisible = true;
      }
    }
    const visible = !narrowing || selfMatch || childVisible;
    el.style.display = visible ? "" : "none";
    if (narrowing && childVisible) {
      el.classList.remove("collapsed");
      const tw = el.querySelector(":scope > .node-row > .twisty");
      if (tw && tw.textContent === "▸") tw.textContent = "▾";
    }
    return visible;
  };
  for (const child of root.children) filterEl(child);
}

export function renderTree(
  container: HTMLElement,
  roots: RegisterNode[],
  toggle: ToggleAsset,
  flyTo: FlyToItem,
  comment: CommentItem,
): void {
  onToggle = toggle;
  onFly = flyTo;
  onComment = comment;
  chipRegistry.clear();
  reviewUpdaters.clear();
  container.innerHTML = "";

  const counts = countNodes(roots);
  const search = document.createElement("input");
  search.type = "search";
  search.className = "tree-search";
  search.placeholder = `Find a layer… (${counts.items} items, ${counts.withAssets} with data)`;

  activeFilter = "all";
  activeQuery = "";
  const filters = document.createElement("div");
  filters.className = "tree-filters";
  const chips: { f: ReviewFilter; label: string; title: string }[] = [
    { f: "all", label: "All", title: "Everything" },
    { f: "flagged", label: "✗ Issues", title: "Layers with a 2D or 3D flagged for a fix" },
    { f: "approved", label: "✓ Done", title: "Layers whose every dimension is signed off" },
    { f: "todo", label: "○ To do", title: "Layers with a dimension not reviewed yet" },
    { f: "excluded", label: "⊘", title: "Fully excluded layers" },
  ];
  const treeWrap = document.createElement("div");
  treeWrap.className = "tree-body";
  for (const c of chips) {
    const b = document.createElement("button");
    b.className = "filter-chip" + (c.f === "all" ? " active" : "");
    b.textContent = c.label;
    b.title = c.title;
    b.addEventListener("click", () => {
      activeFilter = c.f;
      filters.querySelectorAll(".filter-chip").forEach((x) => x.classList.remove("active"));
      b.classList.add("active");
      applyFilters(treeWrap);
    });
    filters.append(b);
  }

  for (const root of roots) treeWrap.append(renderNode(root));

  const setAll = (collapsed: boolean) => {
    treeWrap.querySelectorAll(".node").forEach((el) => {
      if (!el.querySelector(":scope > .children")) return;
      el.classList.toggle("collapsed", collapsed);
      const tw = el.querySelector(":scope > .node-row > .twisty");
      if (tw) tw.textContent = collapsed ? "▸" : "▾";
    });
  };
  const spacer = document.createElement("span");
  spacer.className = "filter-spacer";
  const collapseBtn = document.createElement("button");
  collapseBtn.className = "filter-chip";
  collapseBtn.textContent = "⊟";
  collapseBtn.title = "Collapse all categories";
  collapseBtn.addEventListener("click", () => setAll(true));
  const expandBtn = document.createElement("button");
  expandBtn.className = "filter-chip";
  expandBtn.textContent = "⊞";
  expandBtn.title = "Expand all categories";
  expandBtn.addEventListener("click", () => setAll(false));
  filters.append(spacer, collapseBtn, expandBtn);

  search.addEventListener("input", () => {
    activeQuery = search.value;
    applyFilters(treeWrap);
  });
  container.append(search, filters, treeWrap);

  const countEl = document.getElementById("layers-count");
  if (countEl) countEl.textContent = `${counts.withAssets}/${counts.items}`;
}

function countNodes(roots: RegisterNode[]): { categories: number; items: number; withAssets: number } {
  let categories = 0;
  let items = 0;
  let withAssets = 0;
  const walk = (n: RegisterNode) => {
    if (n.node_type === "category") categories++;
    else items++;
    if (n.assets?.length) withAssets++;
    n.children.forEach(walk);
  };
  roots.forEach(walk);
  return { categories, items, withAssets };
}
